"use client"

import { useEffect, useRef, useState } from "react"
import { X } from "lucide-react"
import { Input } from "@/components/ui/input"
import { httpJson } from "@/lib/http"
import { CRM_API_BASE } from "@/lib/config"

interface Client {
  id: number
  company_name?: string
  first_name?: string
  last_name?: string
  email?: string
}

interface ClientFilterDropdownProps {
  value: string
  onChange: (clientId: string, displayName: string | null) => void
  disabled?: boolean
  variant?: "default" | "compact"
  widthClass?: string
}

const getClientDisplayName = (client: Client): string => {
  if (client.company_name) return client.company_name
  if (client.first_name || client.last_name) {
    return `${client.first_name || ""} ${client.last_name || ""}`.trim()
  }
  return "Unknown Client"
}

export function ClientFilterDropdown({
  value,
  onChange,
  disabled = false,
  variant = "default",
  widthClass,
}: ClientFilterDropdownProps) {
  const [clients, setClients] = useState<Client[]>([])
  const [initialClients, setInitialClients] = useState<Client[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [showDropdown, setShowDropdown] = useState(false)
  const [loading, setLoading] = useState(false)
  const [resolvedSelectedName, setResolvedSelectedName] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const isCompact = variant === "compact"
  const selectedId = value === "all" ? "" : value

  const inputClass = isCompact
    ? "h-7 text-xs border-gray-300 bg-white shadow-sm focus:border-primary focus:ring-1 focus:ring-primary pr-8"
    : "h-10 border border-neutral-300 bg-background focus:border-primary pr-8"

  const containerWidth = widthClass ?? (isCompact ? "w-52" : "w-56")

  const fetchClients = async (term = "", controller?: AbortController) => {
    try {
      setLoading(true)
      const url = term
        ? `${CRM_API_BASE}/client/?q=${encodeURIComponent(term)}&limit=20`
        : `${CRM_API_BASE}/client/?limit=20`
      const response = await httpJson<{ results?: Client[] }>(url, { signal: controller?.signal })
      const list = response.results ?? []
      if (term) {
        setClients(list)
      } else {
        setInitialClients(list)
        setClients(list)
      }
    } catch (error) {
      if (error instanceof Error && (error.message.includes("Request was cancelled") || error.name === "AbortError")) return
      console.error("Error fetching clients:", error)
      setClients([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchClients()
  }, [])

  useEffect(() => {
    if (abortRef.current) abortRef.current.abort()
    if (searchTerm.trim().length < 2) {
      setClients([...initialClients])
      return
    }
    const controller = new AbortController()
    abortRef.current = controller
    const t = setTimeout(() => {
      fetchClients(searchTerm, controller)
    }, 600)
    return () => {
      clearTimeout(t)
      controller.abort()
    }
  }, [searchTerm])

  useEffect(() => {
    if (!selectedId) {
      setResolvedSelectedName(null)
      return
    }
    const found = clients.find((c) => c.id.toString() === selectedId) || initialClients.find((c) => c.id.toString() === selectedId)
    if (found) {
      setResolvedSelectedName(getClientDisplayName(found))
      return
    }
    let cancelled = false
    httpJson<Client>(`${CRM_API_BASE}/client/${selectedId}/`)
      .then((c) => {
        if (!cancelled) setResolvedSelectedName(getClientDisplayName(c))
      })
      .catch(() => {
        if (!cancelled) setResolvedSelectedName(null)
      })
    return () => {
      cancelled = true
    }
  }, [selectedId, clients, initialClients])

  const handleSelect = (clientId: string, displayName: string | null) => {
    setSearchTerm("")
    setShowDropdown(false)
    onChange(clientId || "all", displayName)
  }

  const clearFilter = () => {
    setSearchTerm("")
    setShowDropdown(false)
    onChange("all", null)
  }

  const inputDisplayValue = searchTerm
    || (selectedId && !showDropdown ? (resolvedSelectedName ?? "") : "")
    || (!searchTerm && !selectedId && !showDropdown ? "All Clients" : "")

  return (
    <div className={`relative ${containerWidth}`}>
      <Input
        placeholder={loading ? "Loading clients..." : "All Clients"}
        value={inputDisplayValue}
        onChange={(e) => {
          setSearchTerm(e.target.value)
          setShowDropdown(true)
          if (selectedId) onChange("all", null)
        }}
        onFocus={() => {
          if (initialClients.length === 0) fetchClients()
          setShowDropdown(true)
        }}
        onBlur={() => {
          setTimeout(() => setShowDropdown(false), 200)
        }}
        className={`${inputClass} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        disabled={loading || disabled}
      />
      <button
        type="button"
        onClick={() => {
          if (!showDropdown) {
            setShowDropdown(true)
            setSearchTerm("")
            if (initialClients.length === 0) fetchClients()
          } else {
            setShowDropdown(false)
          }
        }}
        className={`absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer hover:text-primary-foreground p-1 rounded hover:bg-primary disabled:opacity-50 disabled:cursor-not-allowed z-10`}
        disabled={loading || disabled}
      >
        <svg
          className={`${isCompact ? "w-3 h-3" : "w-4 h-4"} transition-transform duration-200 ${showDropdown ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {(searchTerm || selectedId) && (
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            clearFilter()
          }}
          className={`absolute ${isCompact ? "right-7" : "right-9"} top-1/2 -translate-y-1/2 text-gray-600 hover:text-primary-foreground hover:bg-primary rounded p-1 z-20`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
      {showDropdown && (
        <div className="absolute z-[9999] mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-y-auto scrollbar-minimal" style={{ left: "0", width: "100%" }}>
          {searchTerm.trim().length > 0 && searchTerm.trim().length < 2 ? (
            <div className={`p-3 ${isCompact ? "text-xs" : "text-sm"} text-gray-600`}>Type at least 2 characters to search...</div>
          ) : loading ? (
            <div className={`p-3 ${isCompact ? "text-xs" : "text-sm"} text-gray-600`}>Searching clients...</div>
          ) : clients.length === 0 ? (
            <div className={`p-3 ${isCompact ? "text-xs" : "text-sm"} text-gray-600`}>
              {searchTerm.trim() ? `No clients found for "${searchTerm}"` : "No clients available"}
            </div>
          ) : (
            <div>
              <div
                className={`px-4 py-3 hover:bg-blue-50 cursor-pointer ${isCompact ? "text-xs" : "text-sm"} text-gray-900 border-b border-gray-100`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect("", null)}
              >
                All Clients
              </div>
              {clients.map((client) => (
                <div
                  key={client.id}
                  className={`px-4 py-3 hover:bg-blue-50 cursor-pointer ${isCompact ? "text-xs" : "text-sm"} text-gray-900 border-b border-gray-100 last:border-b-0`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(client.id.toString(), getClientDisplayName(client))}
                >
                  {getClientDisplayName(client)}
                </div>
              ))}
              {(searchTerm.trim() === "" ? initialClients.length === 20 : clients.length === 20) && (
                <div className="p-2 text-[11px] text-muted-foreground border-t bg-gray-50">
                  💡 Type a client name to search for more results
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
